import Image from '../../image';
import { ArrowRight } from '../../icon/arrow-right';
import BevSafeText from '../../bev-safe-text';
import { IconLocationPin } from '../../icon';
import { useTranslations } from '@fshr/context/i18n-context';
import { LinkSectionWrapper } from '../link-section-wrapper';
import { TopFiveRecommendations } from '../../types';
import { useRecoTracking } from '../analytics/recoTracker';
import { useRecoImpressionBatchObserver } from '../hooks/useRecoImpression';

type ProductCardsProps = {
	recommendation: TopFiveRecommendations;
	hideLocation?: boolean;
	position: number;
	placement:
		| 'for_you-other_destinations'
		| 'for_you-guest_favourites'
		| 'for_you-highlighted_ancillary';
};

const TitleSection = ({ title, url }: { title: string; url?: string }) => {
	const t = useTranslations();
	return (
		<div className="flex flex-col spacing-stack-xs">
			<div className="flex flex-row spacing-inline-xs">
				<h3 className="text-subtitle1 text-text-primary">
					<BevSafeText>{title}</BevSafeText>
				</h3>
				<div aria-label={t('Navigate to ancilliary landing page')}>
					<ArrowRight width={15} height={15} />
				</div>
			</div>
		</div>
	);
};

const LocationSection = ({ location }: { location?: string }) =>
	location ? (
		<div className="flex flex-row gap-[5px]">
			<IconLocationPin />
			<p className="text-caption text-text-primary">{location}</p>
		</div>
	) : null;

const CostSection = ({ cost }: { cost?: { total: string; type?: string } }) => {
	const t = useTranslations();
	return cost ? (
		<div className="w-full flex flex-row gap-[6px] text-text-secondary">
			<p className="text-subtitle2">{t('From')}</p>
			<div className="flex flex-row spacing-inline-2xs items-end justify-end">
				<span className="text-subtitle2">{cost.total}</span>
				{cost.type === 'PER_PERSON' ? <span className="text-caption">{t('/person')}</span> : null}
			</div>
		</div>
	) : null;
};

const StatusIndicator = ({ status }: { status?: TopFiveRecommendations['status'] }) => {
	if (!status?.message) return null;

	return (
		<div
			className="absolute left-0 right-0 bottom-0 w-full flex justify-center items-center z-1 px-[5px] pt-[6px] pb-[4px] gap-[6px]"
			style={{
				background: 'rgba(3, 3, 3, 0.6)',
				backdropFilter: 'blur(8px)',
				boxShadow: '0 4px 30px rgba(0,0,0,0.3)',
			}}
		>
			<span
				className="inline-block mb-[5px]"
				style={{
					width: 7,
					height: 7,
					borderRadius: '50%',
					background: status.color ?? 'gray',
				}}
			/>
			<p className="text-caption text-text-on-image-primary line-clamp-1">
				<BevSafeText>{status.message}</BevSafeText>
			</p>
		</div>
	);
};

const DescriptionSection = ({ description }: { description?: string }) => {
	return (
		<p className="text-body3 text-text-primary">
			<BevSafeText>{description ?? ''}</BevSafeText>
		</p>
	);
};

export function ProductCard(props: ProductCardsProps) {
	const t = useTranslations();
	const { trackEnhancementRecoClick, trackPropertyRecoClick } = useRecoTracking();

	const {
		image,
		title,
		status,
		description,
		location,
		type,
		price,
		url,
		propertyOrsCode,
		sku,
		datalakeRecoId,
		category,
		subcategory,
		propertyName,
	} = props.recommendation;

	let recoData: any;
	const isAncillary = Boolean(sku);
	if (!isAncillary) {
		recoData = {
			reco_property_code: propertyOrsCode?.toLowerCase(),
			reco_property_name: propertyOrsCode?.toLowerCase() || 'na',
			reco_room_code_array: 'na',
			reco_room_package_code_array: 'na',
			reco_placement: props.placement,
			reco_position: String(props.position),
			property_datalake_reco_id: datalakeRecoId ?? 'na',
		};
	} else {
		recoData = {
			reco_enhancement_property_name: propertyOrsCode?.toLowerCase() || 'na',
			reco_enhancement_sku: sku || 'na',
			reco_enhancement_name: title || 'na',
			reco_placement: props.placement,
			reco_position: String(props.position),
			enhancement_datalake_reco_id: datalakeRecoId ?? 'na',
		};
	}
	const impressionRef = useRecoImpressionBatchObserver(
		recoData,
		!isAncillary ? 'property' : 'ancillary'
	);

	return (
		<LinkSectionWrapper
			className='"w-full cursor-pointer flex flex-row gap-[15px] items-start justify-start"'
			url={url}
			ref={impressionRef}
			propertyOrsCode={propertyOrsCode}
			onClick={() => {
				if (isAncillary) {
					trackEnhancementRecoClick({
						sku: sku || 'na',
						name: title,
						propertyName: propertyName || 'na',
						propertyCode: propertyOrsCode || 'na',
						enhancementDatalakeRecoId: datalakeRecoId || 'na',
						placement: props.placement,
						position: props.position,
					});
				} else {
					trackPropertyRecoClick({
						propertyCode: propertyOrsCode || 'na',
						propertyName: propertyName || 'na',
						propertyDatalakeRecoId: datalakeRecoId || 'na',
						placement: props.placement,
						position: props.position,
					});
				}
			}}
			sku={sku}
		>
			<div className="relative w-[140px] h-[140px] min-w-[140px] min-h-[140px] flex-shrink-0">
				<Image
					roughSize="s"
					src={image.url}
					alt={image.altText ?? t('Product Card')}
					className="w-full h-full object-cover"
				/>
				<StatusIndicator status={status} />
			</div>
			<div className="w-full flex flex-col spacing-stack-2xs">
				{type ? <p className="text-overline text-text-primary capitalize">{type}</p> : null}
				<TitleSection title={title} url={url} />
				<DescriptionSection description={description} />
				{!props.hideLocation && <LocationSection location={location} />}
				<CostSection cost={price} />
			</div>
		</LinkSectionWrapper>
	);
}
